/* eslint-disable no-unused-expressions */
/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import styles from './style';
import { apiCallWizard, addWizard, resetWizard } from '../../../actions/wizards';
import useMyWizard from '../wizards';
import { updateTempState, hideBackButtonState } from '../../../actions/temp';
import {
  wizardsPreviousButtonEvent,
  wizardsNextButtonEvent,
  wizardsValidationErrorEvent,
  wizardsServerErrorEvent,
} from '../../../utils/variables/events';
import { triggerClearCache } from '../../../actions/misc/clearCache';
import { useRouter, useSmall, useGTMDataLayer } from '../../../hooks';
import routes from '../../../utils/variables/routes';
import LoginAsk from './loginAsk';
import {
  agentType,
  statusOk,
  dutchTemplate,
  customerType,
} from '../../../utils/variables';
import {
  createRelativeUrl,
  getRef,
  isValidHttpUrl,
  sourceTextFromData,
  displayErrors,
  displayWarning,
  displaySuccess,
} from '../../../utils/functions';
import {
  getAllData,
  checkComponent,
  resetWizardPayload,
  conditionalProcessBlock,
  getStoreState,
  checkFormValidation,
  getDefaultTrackingParams,
} from '../functions';
import {
  shouldDisplayPopup,
  buildPopupAlertState,
} from '../components/ggilComponents/popupHelper';
import { saveStepData } from '../functions/stepData';
import { getAccessToken } from '../../../storage/accessToken';
import { useOnChangeApiCalls } from '../hooks';
import { getSessionStorage } from '../../../storage/sessionStorage';

const useStyles = makeStyles(styles);

const VALIDATION_TOASTR_TIMEOUT = 3000;

let previousActiveFormIndex = 0;
let lastActiveStep;

const WizardBuilder = ({
  id,
  forms,
  wizardType,
  stepperType,
  defaultNoAuthActiveStep,
  defaultAuthActiveStep,
  action,
  lob,
  type,
  slug,
  isModal,
  disablePadding,
  clearOnWizardLoad,
  disableGridPadding,
  isAuthRequired,
  globalApiCalls,
  extraSettings,
  draftStateIdKey,
  draftStateIdLabel,
  loadSessionStorageKeys,
  askToLogin,
  askMessage,
  ...restProps
}) => {
  const classes = useStyles({ disablePadding });
  const dispatch = useDispatch();
  const router = useRouter();
  const storeGtmDataLayer = useGTMDataLayer();
  const accessToken = getAccessToken();
  const theme = useTheme();
  const { query, location } = router;

  query.stepIndex = isModal ? undefined : query?.stepIndex;

  const { stepIndex: urlStepIndex } = query;

  const reducerStepIndex = useSelector(
    state => state?.wizards?.allInitialWizardData?.stepIndex,
  );

  const stepIndex = urlStepIndex || reducerStepIndex;
  const store = useStore();
  const params = router?.query;

  Object.keys(params).forEach(
    key => (params[key] === undefined || params[key] === null) && delete params[key],
  );

  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const lastEntryPoint = useSelector(state => state.wizards.lastEntryPoint);
  const reloadWizard = useSelector(state => state.temp.reloadWizard);

  const isSmall = useSmall();
  const authId = useSelector(state => state?.auth?.user?.id);

  const customerVerified =
    useSelector(state => state?.auth?.usertype === customerType) && accessToken;

  const draftStateId = useSelector(
    state => state?.wizards?.allInitialWizardData?.[draftStateIdKey],
  );

  const apiCallWizardResponse = useSelector(state => state.wizards.apiCallWizardResponse);

  const isApiCallWizardSuccess = useSelector(
    state => state.wizards.isApiCallWizardSuccess,
  );

  const isGettingWizardSuccess = useSelector(
    state => state.wizards.isGettingWizardSuccess,
  );

  const isAgent =
    useSelector(
      state => state?.auth?.user?.agent?.id && state?.auth?.usertype === agentType,
    ) && accessToken;

  const usertype = useSelector(state => state.auth.usertype);
  const selectedClient = useSelector(state => state.auth?.selectedClient);
  const idVerification = useSelector(state => state.auth?.user?.idVerification) || {};

  const isDutch = theme?.template === dutchTemplate;
  const makeDistinctSuffix = `-${lob?.lob}-${action}`;

  const appSettings = {
    isAuthenticated,
    customerVerified,
    isAgent,
    usertype,
    selectedClient,
    idVerification,
    makeDistinctSuffix,
  };

  const tempStepIndex =
    stepIndex !== null && stepIndex !== undefined
      ? parseInt(stepIndex, 10)
      : parseInt(0, 10);

  const [isRequiredError, setRequiredError] = useState(false);
  const [isValidateExpError, setValidateExpError] = useState(false);

  // Stores the pending action so it only runs after the user confirms leaving the payment step.
  const [pendingPaymentExitAction, setPendingPaymentExitAction] = useState(null);

  // Refs keep the pending action available to event handlers without waiting for a React render.
  const pendingPaymentExitActionRef = useRef(null);
  const allowPaymentNavigationRef = useRef(false);
  const skipPaymentExitConfirmationRef = useRef(false);
  const paymentHeaderBackActionRef = useRef(null);

  const showPaymentExitConfirmation = useCallback(exitDetails => {
    if (pendingPaymentExitActionRef.current) {
      return;
    }

    pendingPaymentExitActionRef.current = exitDetails;
    setPendingPaymentExitAction(exitDetails);
  }, []);

  const allowNextPaymentNavigation = () => {
    allowPaymentNavigationRef.current = true;
  };

  // Navigation to run once the payment guard entry has been popped (see the effect below).
  const afterPaymentGuardPopRef = useRef(null);

  const runAfterLeavingPaymentGuard = navigate => {
    if (!window.history.state?.paymentExitGuard) {
      allowNextPaymentNavigation();
      navigate();
      return;
    }

    // The popstate listener allows and runs `navigate` once the guard is popped.
    afterPaymentGuardPopRef.current = navigate;
    window.history.back();
  };

  const getActiveForms = inputForms => {
    const getActiveFormsStoreState = getStoreState(store, params, extraSettings);

    let filterForms = inputForms?.filter(form => {
      form.isConditionalComponent = form?.isConditionalForm;
      form.conditionalAction = 'show';

      const defaultHide = true;

      const { show } = checkComponent(
        form,
        getActiveFormsStoreState,
        isAuthenticated,
        customerVerified,
        defaultHide,
      );

      return show;
    });

    filterForms = filterForms?.filter(x => !x.hidden) || [];

    if (filterForms) {
      filterForms.sort((a, b) => {
        if (a?.positionNo < b?.positionNo) {
          return -1;
        }

        if (b?.positionNo < a?.positionNo) {
          return 1;
        }

        return 0;
      });
    }

    return filterForms;
  };

  const currentForms = getActiveForms(forms);

  const emptyWizardPayload = resetWizardPayload(
    currentForms,
    isAuthenticated,
    customerVerified,
    makeDistinctSuffix,
  );

  const storeState = getStoreState(store, params, extraSettings);

  const finalMaxForm = currentForms.reduce((acc, val) => {
    return acc?.positionNo > val?.positionNo ? acc : val;
  });

  const filteredMaxSteps = finalMaxForm?.positionNo || 0;
  const newStepIndex = filteredMaxSteps < tempStepIndex ? 0 : tempStepIndex;

  let currentFormIndex = currentForms?.findIndex(x => x.positionNo === newStepIndex);

  let finalStepIndex = newStepIndex;

  if (currentFormIndex < 0) {
    finalStepIndex = 0;
    currentFormIndex = 0;
  }

  if (isModal) {
    finalStepIndex = 0;
  }

  finalStepIndex =
    (currentForms && currentForms[currentFormIndex]?.positionNo) || finalStepIndex;

  const currentFormComponents = currentForms?.[currentFormIndex]?.form?.components || [];

  const paymentComponentSlugs = [
    'DutchPaymentList',
    'GgilPaymentList',
    'GLLCustomPay',
    'CreditCard',
  ];

  const isPaymentStep = currentFormComponents.some(component => {
    if (paymentComponentSlugs.includes(component?.slug)) {
      return true;
    }

    return Object.keys(component || {}).some(key =>
      paymentComponentSlugs.some(slug => key === `${slug}_slug`),
    );
  });

  const formKey = (currentForms && currentForms[currentFormIndex]?.key) || undefined;

  if (formKey) {
    storeState.wizards.allInitialWizardData.formKey = formKey;
  }

  useEffect(() => {
    if (!isPaymentStep) {
      return undefined;
    }

    const { history } = router;
    const leavePageTexts = {
      title: 'Leave this page?',
      message:
        'Are you sure you want to leave this page? Any unsaved changes may be lost.',
    };

    const guardUrl = window.location.href;
    const pushPaymentGuard = () => {
      window.history.pushState(
        { ...(window.history.state || {}), paymentExitGuard: true },
        '',
        guardUrl,
      );
    };

    const activationEvents = ['pointerdown', 'keydown', 'touchstart'];
    const activationListener = { handleEvent: null };
    const removeActivationListeners = () => {
      activationEvents.forEach(eventName =>
        window.removeEventListener(eventName, activationListener, true),
      );
    };
    const addGuardOnActivation = () => {
      removeActivationListeners();
      // After a refresh the browser keeps the guard's state, so don't add it twice.
      if (!window.history.state?.paymentExitGuard) {
        pushPaymentGuard();
      }
    };
    activationListener.handleEvent = addGuardOnActivation;

    if (navigator.userActivation?.hasBeenActive) {
      addGuardOnActivation();
    } else {
      activationEvents.forEach(eventName =>
        window.addEventListener(eventName, activationListener, true),
      );
    }

    // Runs after the router's popstate listener (which history.block has already stopped).
    const handlePaymentGuardPop = event => {
      if (window.location.href !== guardUrl || event.state?.paymentExitGuard) {
        return;
      }

      // The guard was popped on purpose before an in-app navigation.
      if (afterPaymentGuardPopRef.current) {
        const navigate = afterPaymentGuardPopRef.current;
        afterPaymentGuardPopRef.current = null;
        allowNextPaymentNavigation();
        navigate();
        return;
      }

      pushPaymentGuard(); // Browser Back: stay on the page (put the guard back) and ask first.

      if (pendingPaymentExitActionRef.current) {
        return;
      }

      showPaymentExitConfirmation({
        ...leavePageTexts,
        action: () => {
          skipPaymentExitConfirmationRef.current = true; // The previous page may be outside the app, so skip the native unload prompt.
          allowNextPaymentNavigation();
          history.go(-2); // Skip the guard and the payment entry itself.
        },
      });
    };

    window.addEventListener('popstate', handlePaymentGuardPop);

    const handlePaymentHeaderBack = event => {
      event.preventDefault();
      event.stopPropagation();

      if (pendingPaymentExitActionRef.current) {
        return;
      }
      showPaymentExitConfirmation({
        ...leavePageTexts,
        action: () => {
          runAfterLeavingPaymentGuard(() => paymentHeaderBackActionRef.current?.());
          router.replace(routes.index);
        },
      });
    };

    const unblock = history.block((nextLocation, actionType) => {
      const nextPath = `${nextLocation.pathname}${nextLocation.search}${nextLocation.hash}`;
      const currentPath = `${history.location.pathname}${history.location.search}${history.location.hash}`;

      if (
        actionType === 'POP' &&
        nextPath === currentPath &&
        nextLocation.key === history.location.key
      ) {
        return false;
      }

      if (allowPaymentNavigationRef.current) {
        allowPaymentNavigationRef.current = false;
        return undefined;
      }

      if (pendingPaymentExitActionRef.current) {
        return false;
      }

      if (nextPath === currentPath) {
        return undefined;
      }

      // Header links, menus, browser Forward, etc.: stay here until "Yes".
      showPaymentExitConfirmation({
        ...leavePageTexts,
        action: () => {
          skipPaymentExitConfirmationRef.current = true;
          allowNextPaymentNavigation();

          if (actionType === 'POP') {
            history.goBack();
          } else if (actionType === 'REPLACE') {
            history.replace(nextLocation);
          } else {
            history.push(nextLocation);
          }
        },
      });

      return false;
    });

    window.addEventListener('wizard-payment-header-back', handlePaymentHeaderBack);

    return () => {
      unblock();

      removeActivationListeners();
      window.removeEventListener('popstate', handlePaymentGuardPop);
      window.removeEventListener('wizard-payment-header-back', handlePaymentHeaderBack);
    };
  }, [isPaymentStep, router.history, showPaymentExitConfirmation]);

  useEffect(() => {
    if (!isPaymentStep) {
      return undefined;
    }

    // Browser refresh/close: ask the browser to show its native confirmation prompt.
    const handleBeforeUnload = event => {
      if (skipPaymentExitConfirmationRef.current) {
        return;
      }
      event.preventDefault();
      // Modern browsers ignore custom text and display their own generic prompt.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isPaymentStep]);

  const [activeStep, setActiveStep] = React.useState(finalStepIndex || 0);
  const [isGlobalCallsFinish, setGlobalCallsFinish] = useState(false);

  const makeApiCalls = useOnChangeApiCalls(storeState);

  useEffect(() => {
    if (extraSettings?.showBackButton) {
      dispatch(hideBackButtonState.hideBackButtonAction(false));
    }
  }, []);

  useEffect(() => {
    const onErrorCall = () => {
      const filteredCalls = globalApiCalls?.filter(call => {
        const defaultHide = true;
        call.conditionalAction = 'show';

        const { show } = checkComponent(
          call,
          storeState,
          isAuthenticated,
          customerVerified,
          defaultHide,
        );

        return show;
      });

      if (filteredCalls?.some(x => x?.savedKey === 'draftClaim')) {
        const messageObj = {
          label: 'Error loading incomplete claim.',
          message: 'Sorry, we could not retrieve your incomplete claim at this time.',
        };

        const trackingKeys = currentForms?.[currentFormIndex]?.form?.trackingKeys;

        const defaultTrackingParams = getDefaultTrackingParams(storeState, trackingKeys);

        displayErrors(messageObj, classes.toastrSM, {
          timeOut: 0,
        });

        storeGtmDataLayer({
          ...defaultTrackingParams,
          event: wizardsServerErrorEvent,
          slug,
          stepIndex: activeStep,
          title: messageObj?.title,
          message: messageObj?.message,
        });

        setTimeout(() => {
          router.replace(routes?.partiallyFilledClaims?.index);
        }, 1000);
      }
    };

    const onSuccessCall = () => {
      setGlobalCallsFinish(true);
    };

    if (id && !isModal) {
      makeApiCalls({
        apiCalls: globalApiCalls,
        onErrorCall,
        onSuccessCall,
      });

      setTimeout(() => {
        dispatch(updateTempState.updateTempStateAction({ wizardLoaded: id }));
      }, 100);
    } else {
      setGlobalCallsFinish(true);
    }
  }, [id]);

  useEffect(() => {
    const auth = isAuthenticated
      ? {
        userId: authId,
      }
      : {};

    if (!isModal) {
      setActiveStep(finalStepIndex);

      const tempSessionStorageObj = {};

      if (loadSessionStorageKeys && loadSessionStorageKeys?.length > 0) {
        loadSessionStorageKeys?.map(key => {
          const storageValue =
            getSessionStorage(key?.referenceKey, {
              isObject: key?.isObject,
            }) || null;

          if (key?.dontSetIfNull && !storageValue) {
            return;
          }

          tempSessionStorageObj[key?.key] = storageValue;
          return null;
        });
      }

      const appendData = {
        formKey,
        ...params,
        ...tempSessionStorageObj,
        initialCount: 0,
        auth,
      };

      dispatch(addWizard.addInitialWizardDataAction(appendData));
    } else {
      const appendData = {
        initialCount: 0,
        auth,
      };

      dispatch(addWizard.addInitialWizardDataAction(appendData));
    }
  }, [slug, finalStepIndex, isAuthenticated]);

  useEffect(() => {
    previousActiveFormIndex = 0;

    if (isModal) {
      localStorage.setItem('emptyModalWizardPayload', JSON.stringify(emptyWizardPayload));
    }
  }, []);

  const runClearCache = clearCacheKey => {
    const clearCacheKeys = clearCacheKey?.split(',') || [];

    for (let i = 0; i < clearCacheKeys?.length; i++) {
      const key = clearCacheKeys[i];
      dispatch(triggerClearCache(key));
    }
  };
  const handleNext = ({ buttonAction, initialData, toastrTime }) => {
    const thisFormIndex = forms?.findIndex(x => x.positionNo === finalStepIndex);

    const currentForm = forms[thisFormIndex];

    const {
      isRoutingUrlForm,
      routingUrlKey,
      routingStaticUrl,
      positionNo,
      form,
      validations,
      isLatestInitialData,
    } = currentForm;

    if (isLatestInitialData && initialData) {
      initialData = {
        ...initialData,
        state: null,
      };
    }

    const handleNextStoreState =
      initialData?.state || getStoreState(store, params, extraSettings);

    if (buttonAction?.actionType === 'save' || buttonAction?.actionType === 'apiCall') {
      return;
    }

    const filterForms = initialData?.filterForms || getActiveForms(forms);

    previousActiveFormIndex = currentFormIndex;

    let activeValidations = [];

    if (validations && validations?.length > 0) {
      activeValidations = validations?.filter(x => {
        const { activeCondition } = conditionalProcessBlock(
          x,
          handleNextStoreState,
          isAuthenticated,
          customerVerified,
          false,
          true,
        );

        return activeCondition;
      });
    }

    const redirectType = isDutch
      ? buttonAction?.redirectType || form?.next?.redirectType
      : form?.next?.redirectType || buttonAction?.redirectType;

    const redirectUrl = isDutch
      ? buttonAction?.redirectUrl || form?.next?.redirectUrl
      : form?.next?.redirectUrl || buttonAction?.redirectUrl;

    if (form?.next?.resetWizardData) {
      const customerId = handleNextStoreState?.auth?.user?.id || 'none';
      const clearResponse = true;

      dispatch(
        resetWizard.resetWizardDataAction(
          emptyWizardPayload,
          '',
          customerId,
          clearResponse,
        ),
      );
    }

    const {
      dataRequiredValid,
      dataExpValid,
      componentsWithRequireErrors,
      componentsWithExpErrors,
    } =
      initialData?.formValidations ||
      checkFormValidation(handleNextStoreState, currentForm, appSettings);

    const finalForm = filterForms.reduce((acc, val) => {
      return acc?.positionNo > val?.positionNo ? acc : val;
    });

    const finalFormPosition = finalForm?.positionNo;
    let maxCurrentPositionNo = 0;

    const currentActiveStep =
      initialData?.activeStep !== undefined ? initialData?.activeStep : activeStep;

    if (filterForms) {
      maxCurrentPositionNo = Math.max(
        ...filterForms.map(o => (o.positionNo <= currentActiveStep ? o.positionNo : 0)),
        0,
      );
    }

    const nextFormIndex =
      filterForms.findIndex(x => x.positionNo === maxCurrentPositionNo) + 1;

    const defaultTrackingParams = getDefaultTrackingParams(
      handleNextStoreState,
      form?.trackingKeys,
    );

    const nextForms = filterForms;

    if (activeValidations && activeValidations?.length > 0) {
      const title = activeValidations?.[0]?.validationTitle || 'Error';
      const message = activeValidations?.[0]?.validationMessage || 'Error';
      const navigationDetails = activeValidations?.[0]?.navigationDetails || {};

      displayErrors(
        {
          label: title,
          message,
          navigationDetails,
        },
        classes.toastrSM,
        {
          type: activeValidations?.[0]?.validationType,
          timeOut: form?.next?.toastrTime || toastrTime || VALIDATION_TOASTR_TIMEOUT,
        },
      );

      storeGtmDataLayer({
        ...defaultTrackingParams,
        event: wizardsValidationErrorEvent,
        slug,
        stepIndex: activeStep,
        title,
        message,
      });
    } else if (!(dataRequiredValid && dataExpValid)) {
      const title = isDutch
        ? 'You cannot proceed'
        : 'You cannot proceed. The Form is Invalid!';

      const message = isDutch
        ? 'Please fill in required fields'
        : 'Please recheck the form, for the invalid inputs.';

      displayErrors(
        {
          label: title,
          message,
        },
        classes.toastrSM,
        {
          timeOut: form?.next?.toastrTime || toastrTime || VALIDATION_TOASTR_TIMEOUT,
        },
      );

      storeGtmDataLayer({
        ...defaultTrackingParams,
        event: wizardsValidationErrorEvent,
        slug,
        stepIndex: activeStep,
        title,
        message,
        requireFields: componentsWithRequireErrors?.toString(),
        expFields: componentsWithExpErrors?.toString(),
        requireFailed: !dataRequiredValid,
        expFailed: !dataExpValid,
      });
    } else {
      const customAction = form?.next?.customAction;

      if (customAction) {
        if (shouldDisplayPopup(customAction, handleNextStoreState)) {
          dispatch(
            updateTempState.updateTempStateAction({
              alertPopUp: buildPopupAlertState(customAction, {
                dispatch,
                store,
                router,
              }),
            }),
          );

          return;
        }
      }

      // Step-wise quote storage: save this step's fields against the policyToken.
      // Read the store fresh — the token from this step's own API calls (Motor TT:
      // Declaration) isn't in the snapshot taken before those calls ran.
      saveStepData({
        state: getStoreState(store, params, extraSettings),
        currentForm,
        slug,
        forms: filterForms,
      });
      skipPaymentExitConfirmationRef.current = true;
      allowNextPaymentNavigation();

      if (isRoutingUrlForm) {
        if (routingStaticUrl) {
          isValidHttpUrl(routingStaticUrl)
            ? (window.location.href = routingStaticUrl)
            : router.replace(routingStaticUrl);
        } else {
          const newRoute = getRef(handleNextStoreState, routingUrlKey).toString();

          isValidHttpUrl(newRoute)
            ? (window.location.href = newRoute)
            : router.replace(newRoute);
        }
      } else if (redirectType === 'lastEntryPoint' && lastEntryPoint) {
        if (isValidHttpUrl(lastEntryPoint)) {
          window.location.href = lastEntryPoint;
        } else {
          router.replace(lastEntryPoint);
        }
      } else if (redirectType === 'goToStart') {
        router.replace(router.pathname);
      } else if (redirectType === 'redirectUrl' && redirectUrl) {
        const newRedirectUrl = sourceTextFromData(handleNextStoreState, redirectUrl);

        if (isValidHttpUrl(newRedirectUrl)) {
          window.location.href = newRedirectUrl;
        } else {
          router.replace(newRedirectUrl);
        }
      } else if (redirectType === 'home') {
        router.replace(routes.index);
      } else if (
        positionNo <= finalFormPosition &&
        nextForms[nextFormIndex] &&
        nextForms[nextFormIndex].positionNo
      ) {
        if (isModal) {
          setActiveStep(prevActiveStep => prevActiveStep + 1);
        } else {
          const { positionNo: newPositionNo } = nextForms[nextFormIndex];

          let newParams = {
            ...params,
            stepIndex: newPositionNo,
          };

          if (isDutch) {
            const experimentProducts = [
              'motor-quote',
              'cw_motor-quote',
              'home-quote',
              'travel-quote',
            ];

            if (experimentProducts.includes(newParams.slug) && newParams.stepIndex >= 3) {
              newParams = {
                ...newParams,
                isGuest: !isAuthenticated,
              };
            }
          }

          if (form?.next?.urlAppendKeys && form?.next?.urlAppendKeys.length > 0) {
            form?.next?.urlAppendKeys.map(key => {
              newParams[key?.key] =
                getRef(handleNextStoreState, key?.referenceKey) ||
                sourceTextFromData(handleNextStoreState, key?.staticValue);

              return null;
            });
          }

          delete newParams.slug;

          router.replace(createRelativeUrl({ queryObject: newParams }));
        }
      } else {
        const title = 'You have reached the end of the wizard!';
        const message = 'You cannot proceed any further.';

        displayWarning(
          {
            label: title,
            message,
          },
          classes.toastrSM,
        );
      }
    }

    setRequiredError(!dataRequiredValid);
    setValidateExpError(!dataExpValid);
  };
  const handleBackAction = ({ buttonAction, initialData } = {}) => {
    const handleBackStoreState =
      initialData?.state || getStoreState(store, params, extraSettings);

    if (buttonAction?.actionType === 'save' || buttonAction?.actionType === 'apiCall') {
      return;
    }

    const filterForms = initialData?.filterForms || getActiveForms(forms);

    const currentActiveStep =
      initialData?.activeStep !== undefined ? initialData.activeStep : activeStep;

    const thisFormIndex = forms?.findIndex(x => x.positionNo === currentActiveStep);
    const currentForm = forms[thisFormIndex];
    const { previous } = currentForm?.form || {};

    const redirectType = previous?.redirectType;
    const redirectUrl = previous?.redirectUrl;

    if (previous?.resetWizardData) {
      const customerId = handleBackStoreState?.auth?.user?.id || 'none';
      const clearResponse = true;

      dispatch(
        resetWizard.resetWizardDataAction(
          emptyWizardPayload,
          '',
          customerId,
          clearResponse,
        ),
      );
    }

    if (redirectType === 'lastEntryPoint' && lastEntryPoint) {
      if (isValidHttpUrl(lastEntryPoint)) {
        window.location.href = lastEntryPoint;
      } else {
        router.replace(lastEntryPoint);
      }
      return;
    }

    if (redirectType === 'goToStart') {
      router.replace(router.pathname);
      return;
    }

    if (redirectType === 'redirectUrl' && redirectUrl) {
      const newRedirectUrl = sourceTextFromData(handleBackStoreState, redirectUrl);

      if (isValidHttpUrl(newRedirectUrl)) {
        window.location.href = newRedirectUrl;
      } else {
        router.replace(newRedirectUrl);
      }
      return;
    }

    setRequiredError(false);

    if (isModal) {
      if (currentActiveStep === 0) {
        router.replace(routes.index);
      } else {
        setActiveStep(prevActiveStep => prevActiveStep - 1);
      }
      return;
    }

    const currentFilteredIndex = filterForms.findIndex(
      x => x.positionNo === currentActiveStep,
    );

    if (currentFilteredIndex <= 0) {
      router.replace(routes.index);
      return;
    }

    const previousForm = filterForms[currentFilteredIndex - 1];

    if (!previousForm) {
      router.replace(routes.index);
      return;
    }

    const newParams = {
      ...params,
      stepIndex: previousForm.positionNo,
    };

    if (currentForm?.form?.back?.urlAppendKeys?.length > 0) {
      currentForm.form.back.urlAppendKeys.forEach(key => {
        newParams[key?.key] =
          getRef(handleBackStoreState, key?.referenceKey) ||
          sourceTextFromData(handleBackStoreState, key?.staticValue);
      });
    }

    delete newParams.slug;
    router.replace(createRelativeUrl({ queryObject: newParams }));
  };

  const confirmBeforePaymentExit = exitAction => {
    if (skipPaymentExitConfirmationRef.current) {
      skipPaymentExitConfirmationRef.current = false;
      exitAction();
      return;
    }

    if (isPaymentStep) {
      showPaymentExitConfirmation({
        title: 'Go back?',
        message: 'Do you want to go back to the previous step?',
        action: () => runAfterLeavingPaymentGuard(exitAction),
      });

      return;
    }

    exitAction();
  };
  useEffect(() => {
    skipPaymentExitConfirmationRef.current = false;
  }, [router.location.pathname, router.location.search, router.location.hash]);

  const handleBack = backData => {
    confirmBeforePaymentExit(() => handleBackAction(backData));
  };
  const handleApiNext = buttonAction => {
    const handleApiNextStoreState = getStoreState(store, params, extraSettings);
    const filterForms = getActiveForms(forms);
    const currentForm = filterForms?.[currentFormIndex] || {};
    const currentFormConfig = currentForm?.form;

    const defaultTrackingParams = getDefaultTrackingParams(
      handleApiNextStoreState,
      currentFormConfig?.trackingKeys,
    );

    const formValidations = checkFormValidation(
      handleApiNextStoreState,
      currentForm,
      appSettings,
    );

    if (buttonAction?.actionType === 'save') {
      formValidations.dataRequiredValid = true;
    }

    const initialData = {
      activeStep,
      filterForms,
      state: handleApiNextStoreState,
      formValidations,
    };

    const {
      dataRequiredValid,
      dataExpValid,
      componentsWithRequireErrors,
      componentsWithExpErrors,
    } = formValidations || {};

    const apiCalls = buttonAction?.apiCalls;

    if (dataRequiredValid && dataExpValid) {
      const newStoreState = { ...handleApiNextStoreState };

      if (apiCalls && apiCalls.length > 0) {
        getAllData(
          apiCalls,
          apiCallWizard.apiCallWizardAction,
          dispatch,
          newStoreState,
          null,
          null,
          buttonAction,
        )
          .then(resp => {
            if (!resp) {
              handleNext({ buttonAction, initialData });
            } else {
              const responseTypeSuccess =
                resp && resp.length > 0
                  ? resp.every(
                    x => x?.responseType === statusOk || !x.responseType || false,
                  )
                  : false;

              if (responseTypeSuccess) {
                if (
                  buttonAction?.actionType === 'save' ||
                  buttonAction?.actionType === 'apiCall'
                ) {
                  resp?.forEach(x => {
                    const title = 'Success';
                    const message = x?.responseDesc?.message;

                    displaySuccess(
                      {
                        label: title,
                        message,
                      },
                      classes.toastrSM,
                    );
                  });
                }

                handleNext({ buttonAction, initialData });
              }
            }
          })
          .catch(() => { });
      } else {
        handleNext({ buttonAction, initialData });
      }
    } else {
      const title = isDutch
        ? 'You cannot proceed'
        : 'You cannot proceed. The Form is Invalid!';

      const message = isDutch
        ? 'Please fill in required fields'
        : 'Please recheck the form, for the invalid inputs.';

      displayErrors(
        {
          label: title,
          message,
        },
        classes.toastrSM,
        {
          timeOut: VALIDATION_TOASTR_TIMEOUT,
        },
      );

      storeGtmDataLayer({
        ...defaultTrackingParams,
        event: wizardsValidationErrorEvent,
        slug,
        stepIndex: activeStep,
        title,
        message,
        requireFields: componentsWithRequireErrors?.toString(),
        expFields: componentsWithExpErrors?.toString(),
        requireFailed: !dataRequiredValid,
        expFailed: !dataExpValid,
      });
    }

    setRequiredError(!dataRequiredValid);
    setValidateExpError(!dataExpValid);
  };

  const handleApiBackAction = buttonAction => {
    const handleApiBackStoreState = getStoreState(store, params, extraSettings);
    const filterForms = getActiveForms(forms);

    const initialData = {
      activeStep,
      filterForms,
      state: handleApiBackStoreState,
    };

    const apiCalls = buttonAction?.apiCalls;

    if (apiCalls && apiCalls.length > 0) {
      return getAllData(
        apiCalls,
        apiCallWizard.apiCallWizardAction,
        dispatch,
        handleApiBackStoreState,
        null,
        null,
        buttonAction,
      )
        .then(() => handleBackAction({ buttonAction, initialData }))
        .catch(() => { });
    }

    handleBackAction({ buttonAction, initialData });
  };

  const handleApiBack = buttonAction => {
    confirmBeforePaymentExit(() => handleApiBackAction(buttonAction));
  };

  const currentPreviousAction = currentForms?.[currentFormIndex]?.form?.previous;

  paymentHeaderBackActionRef.current = () => {
    if (currentPreviousAction?.apiCalls?.length > 0) {
      return handleApiBackAction(currentPreviousAction);
    }

    return handleBackAction({
      buttonAction: currentPreviousAction,
      initialData: {
        activeStep,
        filterForms: currentForms,
        state: getStoreState(store, params, extraSettings),
      },
    });
  };

  console.log('Current form index:', currentFormIndex);
  console.log('Previous action:', currentPreviousAction);
  console.log('Has previous API calls:', currentPreviousAction?.apiCalls?.length > 0);

  const apiCalls = currentForms && currentForms[currentFormIndex]?.form?.next?.apiCalls;

  const isApiCallWizardResponseTypeOk =
    apiCallWizardResponse &&
      Object.keys(apiCallWizardResponse).length > 0 &&
      apiCalls &&
      apiCalls?.length > 0
      ? apiCalls.every(
        x => apiCallWizardResponse[x?.savedKey]?.responseType === statusOk || false,
      )
      : false;

  const isApiCallSuccess =
    isApiCallWizardSuccess &&
      Object.keys(isApiCallWizardSuccess).length > 0 &&
      apiCalls &&
      apiCalls?.length > 0
      ? apiCalls.every(x => isApiCallWizardSuccess[x?.savedKey])
      : false;

  const _isApiCallWizardPending = useSelector(
    state => state.wizards.isApiCallWizardPending,
  );

  const isApiCallWizardPending =
    _isApiCallWizardPending &&
      Object.keys(_isApiCallWizardPending).length > 0 &&
      apiCalls &&
      apiCalls?.length > 0
      ? apiCalls.map(x => _isApiCallWizardPending[x?.savedKey])
      : null;

  const isApiCallPending =
    isApiCallWizardPending && isApiCallWizardPending.length > 0
      ? isApiCallWizardPending.some(x => x)
      : false;

  if (
    isAuthRequired &&
    !isAuthenticated &&
    newStepIndex >= defaultAuthActiveStep &&
    (newStepIndex < defaultNoAuthActiveStep || !defaultNoAuthActiveStep)
  ) {
    return (
      <Redirect
        to={`${routes.auth.signIn}/?redirect=${location.pathname}${location.search}`}
      />
    );
  }

  const Wizard = useMyWizard(wizardType);

  const askToLoginSession = getSessionStorage('askToLogin', { isObject: true }) || null;

  const checkAsked = askToLoginSession?.[id];

  if (askToLogin && !checkAsked && !isAuthenticated) {
    return <LoginAsk id={id} askMessage={askMessage} />;
  }

  // No: close the confirmation dialog and discard the pending navigation action.
  const cancelPaymentExit = event => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    pendingPaymentExitActionRef.current = null;
    setPendingPaymentExitAction(null);
  };

  // Yes: capture the pending action, close the dialog, then run the action.
  const confirmPaymentExit = () => {
    const exitAction = pendingPaymentExitActionRef.current?.action;

    if (!exitAction) {
      cancelPaymentExit();
      return;
    }

    pendingPaymentExitActionRef.current = null;
    setPendingPaymentExitAction(null);

    exitAction();
  };
  if (wizardType && wizardType.length > 0) {
    return (
      <>
        <Wizard
          forms={currentForms}
          {...restProps}
          handleBack={handleBack}
          handleApiBack={handleApiBack}
          handleApiNext={handleApiNext}
          handleNext={handleNext}
          runClearCache={runClearCache}
          currentFormIndex={isModal ? activeStep : currentFormIndex}
          activeStep={activeStep}
          isRequiredError={isRequiredError}
          setRequiredError={setRequiredError}
          isValidateExpError={isValidateExpError}
          setValidateExpError={setValidateExpError}
          isPending={isApiCallPending}
          stepperType={stepperType}
          action={action}
          lob={lob}
          emptyWizardPayload={emptyWizardPayload}
          isModal={isModal}
          storeState={storeState}
          isCustomer={customerVerified}
          isAuthenticated={isAuthenticated}
          globalApiCalls={globalApiCalls}
          extraSettings={extraSettings}
          params={params}
          draftStateId={draftStateId}
          draftStateIdLabel={draftStateIdLabel}
          lastNextCalls={currentForms[previousActiveFormIndex]?.form?.next?.apiCalls}
          isGlobalCallsFinish={isGlobalCallsFinish}
          disablePadding={disablePadding}
          isSmall={isSmall}
          disableGridPadding={disableGridPadding}
          reloadWizard={reloadWizard}
          slug={slug}
        />

        <Dialog
          open={Boolean(pendingPaymentExitAction)}
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              // return;
            }
          }}
          disableEscapeKeyDown
        >
          <DialogTitle id="payment-exit-dialog-title">
            {pendingPaymentExitAction?.title}
          </DialogTitle>

          <DialogContent>
            <Typography>{pendingPaymentExitAction?.message}</Typography>
          </DialogContent>

          <DialogActions sx={{ position: 'relative', zIndex: 1 }}>
            <Button
              type="button"
              onClick={cancelPaymentExit}
              sx={{ pointerEvents: 'auto', cursor: 'pointer' }}
            >
              No
            </Button>

            <Button
              type="button"
              onClick={confirmPaymentExit}
              variant="contained"
              color="primary"
              sx={{ pointerEvents: 'auto', cursor: 'pointer' }}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return null;
};

export default WizardBuilder;

WizardBuilder.propTypes = {
  className: PropTypes.string,
  wizardType: PropTypes.oneOf(['stepper', 'collapsible']),
  stepperType: PropTypes.oneOf(['default', 'none']),
  forms: PropTypes.arrayOf(PropTypes.object).isRequired,
  lob: PropTypes.object,
  action: PropTypes.string,
};

WizardBuilder.defaultProps = {
  className: '',
  wizardType: 'stepper',
  stepperType: 'default',
  lob: null,
  action: '',
};
